import React, { useState } from 'react';

/**
 * Sidebar.jsx
 * Standalone sidebar navigation component
 * Props: lang, theme, currentView, currentModule, onNavigate, integrationStatus
 */

const Sidebar = ({ lang = 'lo', theme = 'dark', currentView, currentModule, onNavigate, integrationStatus = [] }) => {
  const [collapsed, setCollapsed] = useState(false);

  const i18n = {
    lo: {
      slOverview: 'ພາບລວມ',
      slDashboard: 'ໜ້າຫຼັກ',
      slAnalytics: 'ວິເຄາະ',
      slRevenueAnalytics: 'ວິເຄາະລາຍຮັບ',
      slTopupAnalytics: 'ວິເຄາະ Topup',
      slKpiAnalytics: 'ວິເຄາະ KPI',
      slVehicleAnalytics: 'ວິເຄາະເລື່ອງລົດ',
      slRevenue: 'ໂມດູນລາຍຮັບ',
      slToll: 'ຄ່າທາງດ່ວນ',
      slBridge: 'ຄ່າຂ້າມສະພານ',
      slWim: 'ຄ່າປັບນ້ຳໜັກ',
      slAuction: 'ປະມູນປ້າຍ',
      slTopup: 'Topup OBU/UHF',
      slReports: 'ລາຍງານ',
      slDaily: 'ລາຍງານປະຈຳວັນ',
      slMonthly: 'ລາຍງານປະຈຳເດືອນ',
      slExport: 'ສົ່ງອອກ',
      slSystem: 'ລະບົບ',
      slSettings: 'ຕັ້ງຄ່າ',
      slAudit: 'ບັນທຶກ Audit',
      slApi: 'ສະຖານະ API',
      slIntegration: 'ເຊື່ອມຕໍ່',
      slUsers: 'ຜູ້ໃຊ້ລະບົບ',
      slCollapse: 'ຫຍໍ້ເມນູ',
    },
    th: {
      slOverview: 'ภาพรวม',
      slDashboard: 'แดชบอร์ด',
      slAnalytics: 'วิเคราะห์',
      slRevenueAnalytics: 'วิเคราะห์รายได้',
      slTopupAnalytics: 'วิเคราะห์ Topup',
      slKpiAnalytics: 'วิเคราะห์ KPI',
      slVehicleAnalytics: 'วิเคราะห์เรื่องรถ',
      slRevenue: 'โมดูลรายรับ',
      slToll: 'ค่าทางด่วน',
      slBridge: 'ค่าข้ามสะพาน',
      slWim: 'ค่าปรับน้ำหนัก',
      slAuction: 'ประมูลป้าย',
      slTopup: 'Topup OBU/UHF',
      slReports: 'รายงาน',
      slDaily: 'รายงานประจำวัน',
      slMonthly: 'รายงานประจำเดือน',
      slExport: 'ส่งออก',
      slSystem: 'ระบบ',
      slSettings: 'การตั้งค่า',
      slAudit: 'บันทึก Audit',
      slApi: 'สถานะ API',
      slIntegration: 'การเชื่อมต่อ',
      slUsers: 'ผู้ใช้ระบบ',
      slCollapse: 'ย่อเมนู',
    },
    en: {
      slOverview: 'Overview',
      slDashboard: 'Dashboard',
      slAnalytics: 'Analytics',
      slRevenueAnalytics: 'Revenue Analytics',
      slTopupAnalytics: 'Topup Analytics',
      slKpiAnalytics: 'KPI Analytics',
      slVehicleAnalytics: 'Vehicle Analytics',
      slRevenue: 'Revenue Modules',
      slToll: 'Toll Fee',
      slBridge: 'Bridge Fee',
      slWim: 'WIM Fine',
      slAuction: 'Plate Auction',
      slTopup: 'Topup OBU/UHF',
      slReports: 'Reports',
      slDaily: 'Daily Report',
      slMonthly: 'Monthly Report',
      slExport: 'Export',
      slSystem: 'System',
      slSettings: 'Settings',
      slAudit: 'Audit Log',
      slApi: 'API Status',
      slIntegration: 'Integration',
      slUsers: 'User Management',
      slCollapse: 'Collapse',
    },
  };

  const t = (key) => i18n[lang]?.[key] || i18n.en[key] || key;

  const moduleColors = {
    toll: { bg: 'rgba(250,140,22,.15)', color: '#fa8c16', icon: '◆' },
    bridge: { bg: 'rgba(24,144,255,.15)', color: '#1890ff', icon: '◈' },
    wim: { bg: 'rgba(245,34,45,.15)', color: '#f5222d', icon: '⬡' },
    auction: { bg: 'rgba(82,196,26,.15)', color: '#52c41a', icon: '◆' },
    topup: { bg: 'rgba(146,84,222,.15)', color: '#9254de', icon: '⬡' },
  };

  // Default integration status if not provided
  const defaultStatus = [
    { name: 'smart_gate_prod', status: 'OK' },
    { name: 'bridge_sys', status: 'OK' },
    { name: 'wim_system', status: 'LAG' },
    { name: 'auction_svc', status: 'OK' },
  ];
  const services = integrationStatus.length > 0 ? integrationStatus : defaultStatus;

  const NavItem = ({ view, icon, label, iconBg, iconColor, module: mod }) => {
    const isActive = mod
      ? currentView === 'drilldown' && currentModule === mod
      : currentView === view;

    return (
      <div
        className={`nav-item ${isActive ? 'active' : ''}`}
        onClick={() => onNavigate(mod || view)}
        title={collapsed ? label : ''}
      >
        <div
          className="nav-icon"
          style={{ background: iconBg || 'var(--bg4)', color: iconColor || 'var(--text2)' }}
        >
          {icon}
        </div>
        {!collapsed && <span>{label}</span>}
        {isActive && !collapsed && (
          <div style={{ marginLeft: 'auto', width: '4px', height: '4px', borderRadius: '50%', background: iconColor || 'var(--blue)' }} />
        )}
      </div>
    );
  };

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`} style={{ width: collapsed ? '56px' : undefined, transition: 'width 0.2s ease' }}>
      {/* Overview Section */}
      <div className="nav-section">
        {!collapsed && <div className="nav-label">{t('slOverview')}</div>}
        <NavItem
          view="overview"
          icon="⬡"
          label={t('slDashboard')}
          iconBg="rgba(88,166,255,.15)"
          iconColor="var(--blue)"
        />
      </div>

      {/* Analytics Section */}
      <div className="nav-section">
        {!collapsed && <div className="nav-label">{t('slAnalytics')}</div>}
        <NavItem
          view="analytics-revenue"
          icon="▲"
          label={t('slRevenueAnalytics')}
          iconBg="rgba(57,211,83,.15)"
          iconColor="var(--teal)"
        />
        <NavItem
          view="analytics-topup"
          icon="⬡"
          label={t('slTopupAnalytics')}
          iconBg="rgba(146,84,222,.15)"
          iconColor="#9254de"
        />
        <NavItem
          view="analytics-kpi"
          icon="◈"
          label={t('slKpiAnalytics')}
          iconBg="rgba(24,144,255,.15)"
          iconColor="#1890ff"
        />
        <NavItem
          view="analytics-vehicle"
          icon="🚗"
          label={t('slVehicleAnalytics')}
          iconBg="rgba(250,140,22,.15)"
          iconColor="#fa8c16"
        />
      </div>

      {/* Revenue Modules Section */}
      <div className="nav-section">
        {!collapsed && <div className="nav-label">{t('slRevenue')}</div>}
        {['bridge', 'wim', 'toll', 'auction', 'topup'].map((key) => (
          <NavItem
            key={key}
            module={key}
            icon={moduleColors[key].icon}
            label={t(`sl${key.charAt(0).toUpperCase() + key.slice(1)}`)}
            iconBg={moduleColors[key].bg}
            iconColor={moduleColors[key].color}
          />
        ))}
      </div>

      {/* Reports Section */}
      <div className="nav-section">
        {!collapsed && <div className="nav-label">{t('slReports')}</div>}
        <NavItem
          view="reports"
          icon="▣"
          label={t('slDaily')}
          iconBg="var(--bg4)"
        />
        <NavItem
          view="reports-monthly"
          icon="▣"
          label={t('slMonthly')}
          iconBg="var(--bg4)"
        />
        <NavItem
          view="export"
          icon="↓"
          label={t('slExport')}
          iconBg="var(--bg4)"
        />
      </div>

      {/* System Section */}
      <div className="nav-section">
        {!collapsed && <div className="nav-label">{t('slSystem')}</div>}
        <NavItem
          view="users"
          icon="👤"
          label={t('slUsers')}
          iconBg="rgba(147,84,222,.15)"
          iconColor="#9254de"
        />
        <NavItem
          view="settings"
          icon="⚙"
          label={t('slSettings')}
          iconBg="var(--bg4)"
        />
        <NavItem
          view="settings-audit"
          icon="◎"
          label={t('slAudit')}
          iconBg="var(--bg4)"
        />
        <NavItem
          view="settings-api"
          icon="⬡"
          label={t('slApi')}
          iconBg="var(--bg4)"
        />
      </div>

      {/* Integration Status Widget */}
      {!collapsed && (
        <div style={{
          margin: '16px 12px 0',
          padding: '12px',
          background: 'var(--bg3)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r)',
        }}>
          <div style={{
            fontSize: '10px',
            color: 'var(--text3)',
            textTransform: 'uppercase',
            letterSpacing: '.06em',
            marginBottom: '8px',
          }}>
            {t('slIntegration')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {services.map((svc) => (
              <div key={svc.name} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px' }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: svc.status === 'OK' ? 'var(--teal)' : 'var(--amber)',
                  flexShrink: 0,
                }} />
                <span style={{ color: 'var(--text2)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {svc.name}
                </span>
                <span style={{
                  color: svc.status === 'OK' ? 'var(--teal)' : 'var(--amber)',
                  fontFamily: 'var(--mono)',
                  fontSize: '10px',
                  flexShrink: 0,
                }}>
                  {svc.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Collapse Toggle */}
      <div
        onClick={() => setCollapsed(!collapsed)}
        style={{
          margin: '12px',
          padding: '8px',
          borderRadius: 'var(--r)',
          border: '1px solid var(--border)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: '8px',
          fontSize: '11px',
          color: 'var(--text3)',
          userSelect: 'none',
        }}
        title={t('slCollapse')}
      >
        <span style={{ transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>◀</span>
        {!collapsed && <span>{t('slCollapse')}</span>}
      </div>
    </aside>
  );
};

export default Sidebar;
